import Vue from "vue";
import VueMq from "vue-mq";
import VueRouter from "vue-router";
import { getAppStore } from "./app.store";
import {
  setRuntimeContext,
  getStore,
  IRuntimeContext,
} from "./common/runtime-context";
import {
  initializeServices,
  configureRuntimeContext,
} from "./common/services/service-registry";
import "./common/common-vue";
import "./assets/custom.css";
import "./assets/materialize.css";
import "./assets/entry4sports.css";
import "./user-message/simple-toast.css";
import { initializePWAManager } from "./pwa/pwa-manager";
import { CONFIG_STORE_CONST, IConfigStoreState } from "./config/config-store";
import { i18n } from "./config/il8n";
// Initialize Sentry asynchronously - won't block app startup
import("./config/sentry")
  .then(({ initializeSentry }) => {
    console.log("[E4S Bootstrap] Sentry module loaded, initializing...");
    return initializeSentry();
  })
  .catch((error) => {
    console.error("[E4S Bootstrap] ❌ Sentry initialization failed:", error);
  });
import formControllerFieldDirective from "./common/ui/form/form-controller/form-controller-field-directive";
import { ConfigData } from "./config/config-data";
import { handleResponseMessages } from "./common/handle-http-reponse";
import {
  messageDispatchHelper,
  messageDispatchHelperAdmin,
} from "./user-message/user-message-store";
import { USER_MESSAGE_LEVEL } from "./user-message/user-message-models";
import { AUTH_STORE_CONST } from "./auth/auth-store";

// App version for cache-busting - increment on deployments
export const APP_VERSION = "1.0.0";
console.log(`[E4S Bootstrap] App Version: ${APP_VERSION}`);

// Make APP_VERSION available globally for Sentry release tracking
if (typeof window !== "undefined") {
  (window as any).APP_VERSION = APP_VERSION;
}

/**
 * Function to check for existance of nsl_bypass_cache parameter in URL
 * e.g. https://eire.entry4sports.com/?nsl_bypass_cache=eb17a3e649384e60e3a1abf2d49c6a49#/v2
 * If found, take user to host route.
 * this is to do google oh wolf redirect back to the next end plug in login which
 * then moves it forward to the NSL bypass cache which
 * sends it the word press home page which is the home page where you need to
 * edit the files and stuff and we don't want to do that ,
 */
function stripNSLBypassCache() {
  const urlParams = new URLSearchParams(window.location.search);
  const nslBypassCache = urlParams.get("nsl_bypass_cache");
  if (nslBypassCache) {
    window.location.href = window.location.origin;
  }
}

Vue.use(VueRouter);

// Lazy load LoadingSpinner when needed
Vue.component(
  "LoadingSpinner",
  () => import("./common/ui/loading-spinner.vue"),
);
Vue.directive("form-controller-field", formControllerFieldDirective);
Vue.directive("nick-purple", nickPurple);
Vue.directive("click-outside", clickOutside);

import { LAUNCH_ROUTES_PATHS_V2 } from "./launch/v2/launch-routes-v2";
import { clickOutside, nickPurple } from "./directives/click-outside";
import { loadingState } from "@/launch/v2/loading-state";
import { rootRoutes } from "./root-routes";

export const VUE_MQ_SIZES = {
  MOBILE: {
    name: "mobile",
    size: 700,
  },
  TABLET: {
    name: "tablet",
    size: 1000,
  },
  DESKTOP: {
    name: "desktop",
    size: Infinity,
  },
};

Vue.use(VueMq, {
  breakpoints: {
    // default breakpoints - customize this
    [VUE_MQ_SIZES.MOBILE.name]: VUE_MQ_SIZES.MOBILE.size,
    [VUE_MQ_SIZES.TABLET.name]: VUE_MQ_SIZES.TABLET.size,
    [VUE_MQ_SIZES.DESKTOP.name]: VUE_MQ_SIZES.DESKTOP.size,
  },
  defaultBreakpoint: "sm", // customize this for SSR
});

// window.onerror = function(message, source, line, column, error) {
//     console.log("ONE ERROR HANDLER TO RULE THEM ALL:", message);
// };

Vue.config.errorHandler = function (err, vm, info) {
  // handle error
  // `info` is a Vue-specific error info, e.g. which lifecycle hook
  // the error occurred in

  console.error("🔥 VUE ERROR HANDLER TRIGGERED:", {
    message: err.message,
    stack: err.stack,
    vm: vm?.$options?.name,
    info,
  });

  // Send error to Sentry with additional context (only in production)
  const Sentry = (window as any).Sentry;
  if (import.meta.env.PROD && Sentry) {
    console.log("📡 Sending to Sentry from Vue error handler...");
    Sentry.setTag("error_source", "vue_error_handler");
    Sentry.setTag("vue_lifecycle", info);
    if (vm) {
      Sentry.setTag(
        "component_name",
        vm.$options.name || (vm.$options as any)._componentTag || "anonymous",
      );
      Sentry.setContext("vue_component", {
        name: vm.$options.name,
        props: vm.$props,
        data: vm.$data,
      });
    }
    Sentry.captureException(err);
    console.log("✅ Error sent to Sentry");
  } else {
    console.warn("❌ Sentry not available in Vue error handler");
  }

  // Keep existing admin messaging
  messageDispatchHelperAdmin(
    "Error Message for Admin: " + err.message,
    USER_MESSAGE_LEVEL.ERROR.toString(),
  );
};

Vue.use(VueRouter);

//  For DEV tools, very useful.
Vue.config.performance = !import.meta.env.PROD;

const routes = rootRoutes;

// Type assertion to handle Vue 2.7 compatibility with Vue 3 style components
const routerConfig = {
  routes,
  scrollBehavior() {
    // return desired position
    console.log("router  scrollBehavior()...");
    return { x: 0, y: 0 };
  },
};

export const router = new VueRouter(routerConfig as any);

/*
 * Suppress NavigationDuplicated errors globally.
 * Vue Router 3 throws NavigationDuplicated when pushing the same route.
 * This is not a real error — it's just informational.
 * See: https://github.com/vuejs/vue-router/issues/2881
 */
const originalRouterPush = router.push;
router.push = function (location: any, onComplete?: any, onAbort?: any) {
  return originalRouterPush.call(this, location, onComplete, onAbort).catch((err: any) => {
    if (err && (err.name === 'NavigationDuplicated' || err.type === 8)) {
      return; // silently ignore
    }
    throw err;
  });
};

// Make router available globally for Sentry tracing (avoid circular dependency)
if (typeof window !== "undefined") {
  (window as any).E4S_SENTRY_ROUTER = router;
}

// Initialize service container and runtime context
console.log("[E4S Bootstrap] Initializing services...");

// Configure runtime context first (without store to avoid circular dependency)
setRuntimeContext({
  store: null, // Will be set after store is created
  router: router,
} as any as IRuntimeContext);

initializeServices();

// Now create the store after services are initialized
const store = getAppStore();

// Update runtime context with the actual store
setRuntimeContext({
  store: store,
  router: router,
});

configureRuntimeContext(store, router);
console.log("[E4S Bootstrap] Service initialization complete");

// Initialize PWA Manager after services are set up (only if PWA is enabled)
const isPWAEnabled = import.meta.env.VITE_PWA_ENABLED !== "false";
const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
if (isPWAEnabled) {
  initializePWAManager({
    enablePushNotifications: true,
    enableOfflineDetection: true,
    vapidPublicKey: vapidPublicKey,
  });
  console.log("[E4S Bootstrap] PWA Manager initialized");
} else {
  console.log(
    "[E4S Bootstrap] PWA functionality disabled via VITE_PWA_ENABLED=false",
  );
}

try {
  router.beforeResolve((to, from, next) => {
    console.log("[router.beforeResolve] ENTER", {
      to: to.fullPath,
      from: from.fullPath,
      name: to && to.name,
    });
    loadingState.isRouteLoading = true;
    console.log("[router.beforeResolve] EXIT - proceeding with navigation");
    next(); // Explicitly call next to ensure navigation proceeds
  });
  console.log("[Setup] router.beforeResolve registered successfully");
} catch (err) {
  console.error("[Setup] ERROR registering beforeResolve:", err);
}

router.beforeEach((to, from, next) => {
  console.log(
    "[router.beforeEach] ENTER - setting loadingState.isLoading to true",
    {
      to: to.fullPath,
      from: from.fullPath,
      hasGuard: !!to.meta?.isAuthRequired,
      routeName: to.name,
    },
  );
  loadingState.isLoading = true;
  console.log("[router.beforeEach] EXIT - calling next()");
  next();
});

router.afterEach((to, from) => {
  console.log("[router.afterEach] ENTER", {
    to: to.fullPath,
    from: from.fullPath,
    name: to && to.name,
    isLoadingBefore: loadingState.isLoading,
  });

  loadingState.isLoading = false;
  //  pause for 1 sec then set to false.
  window.setTimeout(() => {
    loadingState.isRouteLoading = false;
  }, 1000);
  // loadingState.isRouteLoading = false;
  console.log("[router.afterEach] setting loadingState.isLoading to false", {
    isLoadingAfter: loadingState.isLoading,
  });

  // Add breadcrumb for navigation tracking (only in production)
  if (import.meta.env.PROD) {
    const Sentry = (window as any).Sentry;
    if (Sentry) {
      Sentry.addBreadcrumb({
        category: "navigation",
        message: `Navigated from ${from.fullPath} to ${to.fullPath}`,
        level: "info",
        data: {
          from: from.fullPath,
          to: to.fullPath,
          route_name: to.name,
          timestamp: new Date().toISOString(),
        },
      });
    }
  }
});

// router.beforeEach((to, from, next) => {
//   // Get the page title from the route meta data that we have defined
//   // See further down below for how we setup this data
//   const title = to.meta.title
// // If the route has a title, set it as the page title of the document/page
//   if (title) {
//     document.title = title
//   }
//   // Continue resolving the route
//   next()
// })

/*
router.beforeEach((to, from, next) => {
  // Get the page title from the route meta data that we have defined
  // See further down below for how we setup this data
  const title = to.meta.title;

  //  Take the title from the parameters
  const titleFromParams = to.params.pageTitle;
  // If the route has a title, set it as the page title of the document/page
  if (title) {
    document.title = title;
  }
  // If we have a title from the params, extend the title with the title
  // from our params
  if (titleFromParams) {
    document.title = `${titleFromParams} - ${document.title}`;
  }
  // Continue resolving the route
  next();
});
*/

export function isLoggedInGuardV2(to: any, _from: any, next: any) {
  // const loginPathV2 = "/" + LAUNCH_ROUTES_PATHS_V2_BASE + "/" + LAUNCH_ROUTES_PATHS_V2.LOGIN_V2
  // isLoggedInGuardGeneric(to, from, next, loginPathV2);
  isLoggedInGuardGeneric(to, next, LAUNCH_ROUTES_PATHS_V2.LOGIN_V2);
}

export function isLoggedInGuard(to: any, _from: any, next: any) {
  // const loginPathV1 = "/#/login";
  // isLoggedInGuardGeneric(to, from, next, loginPathV1);
  // isLoggedInGuardGeneric(to, next, LAUNCH_ROUTES_PATHS.LOGIN_V1);
  isLoggedInGuardGeneric(to, next, LAUNCH_ROUTES_PATHS_V2.LOGIN_V2);
}

export function isLoggedInGuardGeneric(to: any, next: any, loginRoute: string) {
  // const isAuthRequired = to.matched.some((route: RouteConfig) => route.meta.isAuthRequired);

  console.log(".............isLoggedInGuardGeneric");

  Vue.nextTick(() => {
    const title: unknown = to.meta && to.meta.title ? to.meta.title : null;
    if (title) {
      if (typeof title === "string") {
        document.title = title;
        return;
      }
      if (typeof title === "function") {
        document.title = title(to.params);
        return;
      }
      return;
    }
    document.title = "Entry4Sports";
  });

  let isUserLoggedIn = false;
  const store = getStore();

  if (!store) {
    // Store not initialized yet, allow navigation to continue
    next();
    return;
  }

  const configStoreState: IConfigStoreState =
    store.state[CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME];
  const userName = configStoreState.configApp.userInfo.user.display_name;

  if (userName === "" || userName === "ANONYMOUS") {
    //  ANONYMOUS
    const prom = new ConfigData().getAppConfig();
    handleResponseMessages(prom);
    prom.then((response) => {
      if (response.errNo > 0) {
        messageDispatchHelper(
          response.error,
          USER_MESSAGE_LEVEL.ERROR.toString(),
        );
        return;
      }
      store.commit(
        CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME +
          "/" +
          CONFIG_STORE_CONST.CONFIG_MUTATIONS_SET_APP_CONFIG,
        response.data,
      );
      const userNameData = response.data.userInfo.user.display_name;
      const isLoggedIn = userNameData.replace("ANONYMOUS", "").length > 0;
      store.commit(
        AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME +
          "/" +
          AUTH_STORE_CONST.MUTATIONS_LOGGED_IN,
        isLoggedIn,
      );
      if (isLoggedIn) {
        next();
      } else {
        // name: loginRoute, // back to safety route. //
        next({
          name: loginRoute,
          query: {
            redirectFrom: to.fullPath,
          },
        });
      }
    });
    return;
  } else {
    isUserLoggedIn = true;
  }

  // isUserLoggedIn = true;

  // const isUserLoggedIn = process.env.NODE_ENV === "development" ? true : appStore.state[AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME].isLoggedIn;
  // const isUserLoggedIn = appStore.state[CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME].configApp.userInfo.user.name !== "ANONYMOUS";
  // const isUserLoggedIn = false;
  // const isUserLoggedIn = appStore.state[AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME].isLoggedIn;
  if (!isUserLoggedIn) {
    // if (isAuthRequired && !isUserLoggedIn) {
    // appStore.commit(AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME + "/" + AUTH_STORE_CONST.MUTATIONS_SHOW_LOGIN, true);
    next({
      name: "login-v1", // back to safety route //
      query: {
        redirectFrom: to.fullPath,
      },
    });
    return;
  } else {
    next();
  }
  next();
}

// Add global error handlers for unhandled errors
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);

  // Only send to Sentry in production
  if (import.meta.env.PROD) {
    const Sentry = (window as any).Sentry;
    if (Sentry) {
      Sentry.setTag("error_source", "unhandled_promise_rejection");
      Sentry.captureException(event.reason);
    }
  }
});

// Add Sentry user context when store is available (production only)
if (import.meta.env.PROD && getStore()) {
  const Sentry = (window as any).Sentry;
  if (Sentry) {
    // Watch for user login/logout to update Sentry context
    getStore()!.watch(
      (state) =>
        state[CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME]?.configApp?.userInfo
          ?.user?.display_name,
      (userName) => {
        if (userName && userName !== "ANONYMOUS") {
          Sentry.setUser({
            username: userName,
          });
        }
      },
    );
  }
}

// Call the stripNSLBypassCache function before Vue app initialization
console.log(
  "[E4S Bootstrap] Running stripNSLBypassCache before app initialization...",
);
stripNSLBypassCache();

// tslint:disable-next-line:no-unused-expression
new Vue({
  el: "#app",
  router,
  i18n,
  store: getAppStore(),
  // Use an explicit render function so we don't rely on in-DOM template compilation.
  beforeCreate: () => {
    // console.log(`..........Vue.beforeCreate().......Website Mode = [${process.env.NODE_ENV}]`);
    const store = getStore();
    if (store) {
      store.dispatch(
        CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME +
          "/" +
          CONFIG_STORE_CONST.CONFIG_ACTIONS_GET_APP_CONFIG,
      );

      const theme: string = (window as any)
        .E4S_GLOBAL_THEME_IDENTIFIER as string;
      store.commit(
        CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME +
          "/" +
          CONFIG_STORE_CONST.CONFIG_MUTATIONS_SET_THEME,
        theme,
      );
    }

    return window.setTimeout(() => {
      console.log(".....beforeCreate.....");
      return;
    }, 3000);
  },
  mounted() {
    console.log(
      "Vue app mounted, current path: " + this.$route.path,
      this.$route,
    );
  },
  render(h) {
    return h("router-view");
  },
});
